import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ResponseModel } from '../interfaces/base-response/base-response.interface';

@Injectable({
  providedIn: 'root'
})
export class BaseService<T> {
  public baseUrl: string = '';
  public headers: HttpHeaders;

  constructor(private http: HttpClient, @Inject('endpoint') endpoint: string) {
    this.baseUrl = `${environment.apiUrl}/${endpoint}`;
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json',
      // Agrega otros encabezados según sea necesario, por ejemplo, Authorization
    });
  }

  /**
   * Obtiene una lista de entidades o una entidad específica por ID
   * @param id?: number - opcional, si se proporciona, obtiene una entidad específica
   * @param action: string - acción a realizar en la entidad
   * @returns Observable<ResponseModel<T>> o Observable<ResponseModel<T[]>>
   */
  public get(id?: number, action = ''): Observable<ResponseModel<T> | ResponseModel<T[]>> {
    const url = id ? `${this.baseUrl}/${action}/${id}` : `${this.baseUrl}/${action}`;
    return this.http.get<ResponseModel<T> | ResponseModel<T[]>>(url, { headers: this.headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Crea una entidad
   * @param obj: T - entidad a ser creada
   * @param action: string - acción a realizar en la entidad
   * @returns Observable<ResponseModel<T>>
   */
  public post(obj: T, action = ''): Observable<ResponseModel<T>> {
    return this.http.post<ResponseModel<T>>(`${this.baseUrl}/${action}`, obj, { headers: this.headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Actualiza una entidad
   * @param id: number - ID de la entidad a ser actualizada
   * @param obj: T - entidad con los nuevos valores
   * @param action: string - acción a realizar en la entidad
   * @returns Observable<ResponseModel<T>>
   */
  public put(id: number, obj: T, action = ''): Observable<ResponseModel<T>> {
    return this.http.put<ResponseModel<T>>(`${this.baseUrl}/${action}/${id}`, obj, { headers: this.headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Elimina una entidad
   * @param id: number - ID de la entidad a ser eliminada
   * @param action: string - acción a realizar en la entidad
   * @returns Observable<void>
   */
  public delete(id: number, action = ''): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${action}/${id}`, { headers: this.headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Maneja errores HTTP
   * @param error - el error HTTP
   * @returns Observable<never>
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = '¡Ocurrió un error desconocido!';
    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Error del lado del servidor
      errorMessage = `Código de Error: ${error.status}\nMensaje: ${error.message}`;
    }
    return throwError(() => new Error(errorMessage));
  }
}
